import React from "react";
import { X } from "lucide-react";
import { colorOf } from "@/lib/schApi";
import { field } from "@/components/nds/windows/finance/parts";
import ResidentMultiPicker from "@/components/nds/windows/sch/ResidentMultiPicker";

/** Multi-pick of whose schedules to overlay; "me" is labelled My schedule. */
function PeoplePick({ label, testid, list, picked, me, onChange, empty }) {
  const name = (id) => (id === me ? "My schedule" : list.find((p) => p.id === id)?.name || "Unknown");
  return (
    <div data-testid={`sch-filter-${testid}-pick`} className="space-y-1">
      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted">{label}</div>
      <div className="flex flex-wrap gap-1">
        {picked.map((id) => (
          <span key={id} data-testid={`sch-filter-${testid}-chip-${id}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-nex-gold/40 text-nex-gold rounded-sm text-[10px]">
            {name(id)}<button type="button" onClick={() => onChange(picked.filter((x) => x !== id))} data-testid={`sch-filter-${testid}-remove-${id}`} aria-label={`Remove ${name(id)}`}><X className="h-3 w-3" /></button>
          </span>
        ))}
        {me && !picked.length && empty && <span data-testid={`sch-filter-${testid}-default`} className="text-[10px] text-nex-muted">Showing: My schedule</span>}
      </div>
      <select data-testid={`sch-filter-${testid}`} className={`${field} w-full`} value="" onChange={(e) => e.target.value && onChange([...picked, e.target.value])}>
        <option value="">+ Add {label.toLowerCase()}…</option>
        {me && !picked.includes(me) && <option value={me}>My schedule</option>}
        {list.filter((p) => p.id !== me && !picked.includes(p.id)).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
    </div>
  );
}

/** Event-type checkboxes for one category. */
export function TypeChecklist({ meta, category, value, onChange, big }) {
  const types = value.types || [];
  const toggle = (k) => onChange({ ...value, types: types.includes(k) ? types.filter((x) => x !== k) : [...types, k] });
  return Object.entries(meta.event_types).filter(([, t]) => t.category === category).map(([k, t]) => (
    <label key={k} className={`flex items-center gap-1.5 text-white/80 cursor-pointer ${big ? "py-2 text-sm gap-3" : "pl-3"}`}>
      <input type="checkbox" data-testid={`sch-filter-type-${k}`} checked={types.includes(k)} onChange={() => toggle(k)} />{t.label}
    </label>
  ));
}

/** Whose schedules (staff / residents), location, date range, cancelled. */
export function ScheduleFields({ meta, value, onChange, me }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <>
      <PeoplePick label="Staff" testid="staff" list={meta.staff} picked={value.staff_ids || []} me={me} empty={!(value.resident_ids || []).length} onChange={(v) => set("staff_ids", v)} />
      <ResidentMultiPicker value={value.resident_ids || []} onChange={(v) => set("resident_ids", v)} />
      <select data-testid="sch-filter-location" className={`${field} w-full`} value={value.location || ""} onChange={(e) => set("location", e.target.value)}>
        <option value="">Any location</option>{meta.locations.map((l) => <option key={l}>{l}</option>)}
      </select>
      <label className="block text-nex-muted">From<input type="date" data-testid="sch-filter-from" className={`${field} w-full mt-1`} value={value.from || ""} onChange={(e) => set("from", e.target.value)} /></label>
      <label className="block text-nex-muted">To<input type="date" data-testid="sch-filter-to" className={`${field} w-full mt-1`} value={value.to || ""} onChange={(e) => set("to", e.target.value)} /></label>
      <label className="flex items-center gap-1.5 text-white/80"><input type="checkbox" data-testid="sch-filter-show-cancelled" checked={!!value.show_cancelled} onChange={(e) => set("show_cancelled", e.target.checked)} /> Show cancelled</label>
    </>
  );
}

/** Sidebar filters: event type (by category), whose schedules (staff / residents), location, date range. */
export default function Filters({ meta, value, onChange, me, onClear }) {
  return (
    <aside data-testid="sch-filters" className="w-full md:w-56 shrink-0 border-r border-white/10 overflow-y-auto p-3 space-y-3 text-xs">
      <div className="flex justify-between items-center">
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted">Filters</span>
        <button type="button" onClick={onClear} data-testid="sch-filters-clear" className="text-nex-gold text-[10px]">Clear</button>
      </div>
      {Object.entries(meta.categories).map(([ck, cl]) => (
        <div key={ck} className="space-y-0.5">
          <div className="flex items-center gap-1.5 text-[10px] text-nex-muted uppercase tracking-wider"><span className="h-2 w-2 rounded-full" style={{ background: colorOf({ category: ck }) }} />{cl}</div>
          <TypeChecklist meta={meta} category={ck} value={value} onChange={onChange} />
        </div>
      ))}
      <ScheduleFields meta={meta} value={value} onChange={onChange} me={me} />
    </aside>
  );
}
